import styled, { css, keyframes } from 'styled-components'

import {
  Button,
  Text,
  truncateLines,
  truncateStyles,
  TableComponents,
  Image as ImageUnstyled,
  ImageBaseComponent,
  Tooltip,
  Checkbox,
  CheckboxInput,
} from '@sketch/components'

import { ReactComponent as EllipsisIcon } from '@sketch/icons/dots-3-horizontal-24'
import { ReactComponent as InformationIcon } from '@sketch/icons/information-filled-outline-17'

import { breakpoint } from '@sketch/global-styles'

/* stylelint-disable no-descending-specificity */
const commonIconContainerStyles = css`
  position: absolute;

  display: flex;
  padding: 8px;

  background-color: ${({ theme }) => theme.colors.background.secondary.A};
  z-index: ${({ theme }) => theme.zIndex[1]};

  ::before,
  ::after {
    content: '';
    position: absolute;
    width: 20px;
    height: 20px;
  }
`

export const TopLeftIcons = styled.div(
  ({ theme }) => css`
    ${commonIconContainerStyles}

    top: 3px;
    left: 1px;
    border-radius: ${theme.radii.xxlarge} 0 ${theme.radii.xxlarge} 0;
    box-shadow: 0 2px 4px ${theme.colors.thumbnails.shadowIcons};

    /* These are two small inverter borders to create the inverted rounded corners the designs has*/
    /* Top Right Inverted Border */
    ::before {
      top: 0;
      right: -20px;
      border-radius: ${theme.radii.xlarge} 0 0 0;
    }

    /* Bottom Left Inverted Border */
    ::after {
      bottom: -20px;
      left: 0;
      border-radius: ${theme.radii.xlarge} 0 0 0;
    }
  `
)

export const BottomRightIcons = styled.div(
  ({ theme }) => css`
    ${commonIconContainerStyles}

    justify-content: space-evenly;
    gap: 8px;

    bottom: 0px;
    right: 0px;
    border-radius: ${theme.radii.xxlarge} 0 ${theme.radii.xxlarge} 0;
    box-shadow: -2px -2px 4px ${theme.colors.thumbnails.shadowIcons};
    border: none;
    z-index: ${theme.zIndex[2]};

    /* These are two small inverter borders to create the inverted rounded corners the designs has*/
    /* Top Right Inverted Border */
    ::before {
      top: -20px;
      right: 0;
      border-radius: 0 0 ${theme.radii.xlarge} 0;
      box-shadow: 0 10px 0 0 ${theme.colors.background.secondary.A};
    }

    /* Bottom Left Inverted Border */
    ::after {
      bottom: 0;
      left: -20px;
      border-radius: 0 0 ${theme.radii.xlarge} 0;
      box-shadow: 10px 0 0 0 ${theme.colors.background.secondary.A};
    }
  `
)

export const StatusIcon = styled.svg.attrs({
  role: 'img',
})`
  position: absolute;

  top: 0;
  bottom: 0;
  left: 0;
  right: 0;

  /* This color should be "Light/colors.foreground.secondary.4" but given the icon composition */
  /* Opacity needs to be applied to the whole object */
  color: ${({ theme }) => theme.colors.foreground.secondary.A};
  opacity: 0.55;

  margin: auto;
`

export const InfoIcon = styled(InformationIcon)`
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
  width: 17px;
  height: 17px;
  vertical-align: text-top;
`

export const AutosaveWrapper = styled.div`
  display: flex;
  gap: 4px;
  align-items: center;
`

export const ImageWrapper = styled.div.attrs({
  'data-testid': 'DocumentItemImageWrapper',
})`
  position: relative;
  overflow: hidden;
  padding: 16px;

  flex-shrink: 0;

  .drag-and-drop-ghost &&& {
    background: ${({ theme }) => theme.colors.background.secondary.A};
  }

  /* Make sure the Wrapper is a responsive rectangle  */
  :before {
    content: '';
    display: block;
    padding-bottom: 100%;
  }

  :after {
    content: '';
    position: absolute;

    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
  }
`

export const TableImageWrapper = styled(ImageWrapper)(
  ({ theme }) => css`
    /* We need to make sure the size is set to prevent the image from expanding/shrinking */
    width: 32px;
    height: 32px;

    border-radius: ${theme.radii.medium};
    background-color: ${theme.colors.state.hover};

    /* Maintain the radius in the inside border */
    ::after {
      border-radius: ${theme.radii.medium};
    }
  `
)

export const Image = styled(ImageUnstyled)`
  position: absolute;

  top: 0;
  right: 0;
  bottom: 0;
  left: 0;

  display: flex;
  justify-content: center;
  align-items: center;

  transition: opacity ${({ theme }) => theme.transitions.duration[2]};
`

export const ImageElement = styled(ImageBaseComponent).attrs({
  draggable: false,
})`
  object-fit: contain;
  max-width: 100%;
  max-height: 100%;

  /* stylelint-disable font-family-no-missing-generic-family-keyword */
  font-family: 'object-fit: contain;';
`

// TODO: Replace <h1> with a more semantically appropriate HTML element
export const Name = styled.h1`
  /* Fix the default margin of the element */
  margin: 0;

  color: ${({ theme }) => theme.colors.foreground.secondary.A};

  font-size: 0.875rem;
  font-weight: 500;
  line-height: ${({ theme }) => theme.lineHeights.default};
  white-space: break-spaces;
  word-break: break-word;

  ${truncateLines(2)};
`

export const SubtitleTextWrapper = styled(Text.Span).attrs({
  textStyle: 'copy.quaternary.standard.C',
})`
  display: inline-flex;
  align-content: center;
  gap: 8px;
  font-weight: 400;
  justify-content: center;

  b {
    font-weight: 400;
    color: ${({ theme }) => theme.colors.foreground.secondary.B};
  }
`

/**
 * Dropdown Section
 */
export const DropdownButton = styled(Button).attrs({
  variant: 'secondary',
  size: '32',
})`
  background-clip: padding-box;
  padding: 4px 8px;
`

export const DropdownButtonEllipsis = styled(EllipsisIcon)`
  width: 24px;
  height: 24px;
  color: ${({ theme }) => theme.colors.foreground.secondary.D};
`

export const DropdownButtonWrapper = styled.div`
  position: absolute;

  top: 9px;
  right: 9px;

  opacity: 0;
  transform: translate3d(0, -5px, 0);
  transition: ${({ theme }) => `
    opacity ${theme.transitions.duration[0]} ${theme.transitions.timing.easeInOut},
    transform ${theme.transitions.duration[0]} ${theme.transitions.timing.easeInOut};
  `};

  /* Prevent the button from hiding when the dropdown is open */
  [aria-expanded='true'] ${DropdownButton} {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
  &:has([aria-expanded='true']),
  &:has([aria-expanded='true']):focus-within {
    opacity: 1;
    transform: translate3d(0, 0, 0);
    /* When the dropdown is open we want the button and the dropdown to be over the other z-indexed content */
    z-index: 1000;
  }
`

export const SelectedCheckbox = styled(Checkbox)`
  line-height: 1;

  ${CheckboxInput} {
    margin: 0;
    border-color: ${({ theme }) => theme.colors.shadow.inner};
    border-radius: ${({ theme }) => theme.radii.base};
    box-shadow: 0 1px 2px 0 ${({ theme }) => theme.colors.shadow.inner};
  }
`

export const SelectedCheckboxWrapper = styled.label`
  padding: 10px; /* stylelint-disable-line scales/space */
`

export const SelectedCheckboxFloatingWrapper = styled(SelectedCheckboxWrapper)`
  position: absolute;
  bottom: 4px;
  left: 4px;
  z-index: 50;

  /* Fix the additional margin around the element */
  line-height: 1;

  /* Initially hidden so we can show it when hover */
  opacity: 0;
  visibility: hidden;

  transition: ${({ theme }) => `
    opacity ${theme.transitions.duration[0]} ${theme.transitions.timing.easeInOut},
  `};
`

export const GridFocusStyles = css`
  ${DropdownButtonWrapper} {
    opacity: 1;
    transform: translate3d(0, 0, 0);
  }
`

/**
 * Wrapper Section
 */
export const commonWrapper = css`
  & .shrink {
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  & .keep-size {
    flex-shrink: 0;
  }

  & .wrap {
    overflow-wrap: break-word;
    word-wrap: break-word;
    hyphens: auto;
  }

  &[role='button'] {
    cursor: pointer;
  }
`

export const GridWrapper = styled.section(
  ({ theme }) => css`
    ${commonWrapper};

    display: flex;
    flex-direction: column;

    position: relative;

    && ${/* sc-selector */ Name}, ${/* sc-selector */ SubtitleTextWrapper} {
      text-align: center;
    }

    && ${ImageWrapper} {
      border-radius: ${theme.radii.xxlarge};

      background-color: ${theme.colors.background.tertiary.D};

      box-shadow: ${theme.colors.thumbnails.shadow};
      border: 4px solid
        ${theme._name === 'light'
          ? theme.colors.background.secondary.A
          : theme.colors.foreground.primary.B};
      transition: box-shadow ${theme.transitions.duration[2]}
        ${theme.transitions.timing.easeInOut};

      &:hover {
        box-shadow: ${theme.colors.thumbnails.shadowHover};
      }

      /* Maintain the radius in the inside border */
      ::after {
        border-radius: ${theme.radii.xxlarge};
      }
    }

    ${StatusIcon} {
      width: 64px;
      height: 64px;
    }

    @media (hover: none) {
      ${GridFocusStyles}
    }

    @media (hover: hover) {
      &:hover,
      &:focus,
      &:focus-within {
        ${GridFocusStyles}

        ${SelectedCheckboxFloatingWrapper} {
          opacity: 1;
          visibility: visible;
        }
      }
    }
  `
)

export interface IconWrapperProps {
  $isSelected?: boolean
  $showSelectionCheckbox?: boolean
}

export const IconsWrapper = styled.div<IconWrapperProps>(
  ({ theme, $isSelected, $showSelectionCheckbox }) => css`
    position: relative;

    /* Margin from image wrapper to the document name */
    margin-bottom: 8px; /* stylelint-disable-line scales/space */

    && ${ImageWrapper} {
      border-radius: ${theme.radii.xxlarge};

      background-color: ${theme.colors.background.tertiary.D};

      box-shadow: ${theme.colors.thumbnails.shadow};
      border: 4px solid
        ${theme._name === 'light'
          ? theme.colors.background.secondary.A
          : theme.colors.foreground.primary.B};
      transition: box-shadow ${theme.transitions.duration[2]}
        ${theme.transitions.timing.easeInOut};
    }

    ${($isSelected || $showSelectionCheckbox) &&
    css`
      && ${SelectedCheckboxFloatingWrapper} {
        opacity: 1;
        visibility: visible;
      }

      && ${Image} {
        opacity: 0.84;
      }
    `}
  `
)

export const TableWrapper = styled(TableComponents.TableRow)<{
  $isSelected?: boolean
}>(
  ({ theme, $isSelected }) => css`
    ${commonWrapper};

    ${$isSelected &&
    css`
      &&&& {
        background-color: ${({ theme }) => theme.colors.sketch.F};
      }
    `}

    ${StatusIcon} {
      width: 17px;
      height: 17px;
    }

    ${breakpoint('base', 'sm')`
    overflow: auto;
    width: 100vw;

    ${TableComponents.TableCellSticky} {
      z-index: 2;
      right: 0;
    }

    ${TableComponents.TableHeaderCell}:last-child {
      right: 0;
      width: 60px;

      ${breakpoint('base', 'sm')`
        color: ${theme.colors.background.secondary.A}; /** Just for hiding the text */
      `}
    }
  `}
  `
)

export const TableCell = styled(TableComponents.TableCell)`
  flex: 1;

  :last-child {
    flex-grow: 0;
    flex-shrink: 0;
    padding-right: 12px;
    min-width: 64px;
  }
`

export const DropdownWrapper = styled.span`
  line-height: 1;
`

export const TableAvatarNames = styled.span`
  word-break: break-word;
  ${truncateLines(2)};

  margin-left: 8px;
`

export const GridPlaceholderWrapper = styled(GridWrapper)(
  ({ theme }) => css`
    margin-bottom: 44px;

    ${/* sc-selector */ ImageWrapper}:after {
      box-shadow: none;
    }

    && ${ImageWrapper} {
      background-color: ${theme.colors.background.tertiary.C};
    }
  `
)

export const TablePlaceholderWrapper = styled(TableWrapper)`
  ${/* sc-selector */ ImageWrapper}:after {
    box-shadow: none;
  }
`
export const TooltipStyled = styled(Tooltip)`
  ${truncateStyles};
`

const reveal = keyframes`
    0% {
      opacity: 0;
      transform: translateY(-5px)
    }
    100% {
      opacity: 1;
      transform: translateY(0)
    }
  `

// We want to show a tooltip when a share is dragged over the droppable area.
// Unfortunately `@sketch/components/Tooltip` is tightly coupled to the idea of
// having a "trigger" area that will show the toolip on hover so we can't
// easily re-use that component.
// Instead we have taken a minimal subset of the styles to create a custom
// component that looks similar, and can be shown when a share is dragged over
// the droppable area.
export const OnDraggedOverTooltip = styled.div(
  ({
    theme: {
      colors,
      radii,
      fontSizes,
      zIndex,
      transitions: { duration, timing },
    },
  }) => css`
    background: ${colors.ui.tooltips.background};
    color: ${colors.ui.tooltips.title};
    border-radius: ${radii.xlarge};
    font-size: ${fontSizes.C};
    animation: ${reveal} ${duration[4]} ${timing.easeOutExponential};
    padding: 8px 12px;
    position: absolute;
    top: -42px;
    left: 50%;
    margin-left: -60px;
    box-shadow: 0 2px 4px 0 hsla(0, 0%, 0%, 0.04);
    z-index: ${zIndex[8]};
  `
)

export const DroppableTableWrapper = styled(TableWrapper)<{
  $isDraggedOver?: boolean
}>`
  position: relative;

  ${({ $isDraggedOver }) =>
    $isDraggedOver &&
    css`
      outline: 4px solid ${({ theme }) => theme.colors.sketch.D};
      outline-offset: -4px;
    `}
`

export const DroppableTableWrapperDragTooltip = styled(OnDraggedOverTooltip)`
  top: auto;
  margin-top: -52px;
`

export const NameWrapper = styled.div<{ type?: 'table' }>`
  display: flex;
  gap: 12px;
  align-items: center;
  white-space: pre-wrap;

  position: relative;

  ${({ type }) =>
    type === 'table' &&
    css`
      & > div {
        display: flex;
        align-items: center;
      }
    `}
`

const animation = keyframes`
  0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`

export const SelectedShadowWrapper = styled.div`
  position: absolute;
  inset: -4px;
  animation: ${animation} 0.2s linear forwards;
`

export const SelectedShadow = styled.div`
  aspect-ratio: 1;
  border-radius: ${p => p.theme.radii.xxxlarge};
  padding: 4px;
  background: linear-gradient(
    135deg,
    ${p => p.theme.colors.sketch.A},
    ${p => p.theme.colors.sketch.D}
  );
  mask:
    linear-gradient(#fff 0 0) content-box,
    linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
`
